import { applyProposal, describeChanges, netImpact } from '../CorrectionProposal/correctionProposal';
/** Matches the DayTimeline palette so a day reads the same everywhere. */
export const DIFF_WORK_COLOR = '#4CAF50';
export const DIFF_BREAK_COLOR = '#F59E0B';
const ms = (iso) => new Date(iso).getTime();
const mins = (a, b) => Math.max(0, Math.round((b - a) / 60000));
/**
 * Interleaves work and break spans across the entry, exactly as DayTimeline
 * does, then scales each to the shared axis.
 */
export const buildBar = (snapshot, axisFrom, axisSpan) => {
    const start = ms(snapshot.start_time);
    const end = snapshot.end_time ? ms(snapshot.end_time) : start;
    const pct = (durationMs) => (axisSpan > 0 ? (durationMs / axisSpan) * 100 : 0);
    const breaks = (snapshot.breaks ?? [])
        .filter((b) => !!b.end_time)
        .map((b) => ({ start: ms(b.start_time), end: ms(b.end_time), name: b.break_type_name || 'Break' }))
        .sort((a, b) => a.start - b.start);
    const segments = [];
    // Leading gap when this bar starts later than the axis does.
    if (start > axisFrom) {
        segments.push({ key: 'lead', kind: 'empty', widthPct: pct(start - axisFrom), label: '', durationMinutes: 0 });
    }
    let cursor = start;
    let breakTotal = 0;
    breaks.forEach((b, i) => {
        if (b.start > cursor) {
            segments.push({
                key: `work-${i}`,
                kind: 'work',
                widthPct: pct(b.start - cursor),
                label: 'Working',
                durationMinutes: mins(cursor, b.start),
            });
        }
        segments.push({
            key: `break-${i}`,
            kind: 'break',
            widthPct: pct(b.end - b.start),
            label: b.name,
            durationMinutes: mins(b.start, b.end),
        });
        breakTotal += mins(b.start, b.end);
        cursor = b.end;
    });
    if (end > cursor) {
        segments.push({
            key: 'work-last',
            kind: 'work',
            widthPct: pct(end - cursor),
            label: 'Working',
            durationMinutes: mins(cursor, end),
        });
    }
    // Trailing gap when the other version of the day runs longer than this one.
    if (end < axisFrom + axisSpan) {
        segments.push({
            key: 'trail',
            kind: 'empty',
            widthPct: pct(axisFrom + axisSpan - end),
            label: '',
            durationMinutes: 0,
        });
    }
    return { segments, netMinutes: Math.max(0, mins(start, end) - breakTotal) };
};
/** Builds everything the diff needs from the stored snapshot and proposal. */
export const buildCorrectionDiff = (snapshot, proposal) => {
    const after = applyProposal(snapshot, proposal);
    const starts = [ms(snapshot.start_time), ms(after.start_time)];
    const ends = [
        snapshot.end_time ? ms(snapshot.end_time) : ms(snapshot.start_time),
        after.end_time ? ms(after.end_time) : ms(after.start_time),
    ];
    const axisFrom = Math.min(...starts);
    const axisTo = Math.max(...ends);
    const axisSpan = Math.max(1, axisTo - axisFrom);
    return {
        before: buildBar(snapshot, axisFrom, axisSpan),
        after: buildBar(after, axisFrom, axisSpan),
        changes: describeChanges(snapshot, proposal),
        impact: netImpact(snapshot, proposal),
        axisStart: new Date(axisFrom).toISOString(),
        axisMid: new Date(axisFrom + axisSpan / 2).toISOString(),
        axisEnd: new Date(axisTo).toISOString(),
    };
};
/**
 * One day's bar drawn on its OWN axis — the whole entry fills the width.
 *
 * Used where there is nothing to compare against: previewing the shift an
 * employee is proposing when reporting missing time, and showing the day being
 * edited in the correction editor.
 */
export const buildSnapshotBar = (snapshot) => {
    const from = ms(snapshot.start_time);
    const to = snapshot.end_time ? ms(snapshot.end_time) : from;
    return buildBar(snapshot, from, Math.max(1, to - from));
};
/** Total break minutes on a snapshot, for a preview's summary row. */
export const snapshotBreakMinutes = (snapshot) => (snapshot.breaks ?? []).filter((b) => !!b.end_time).reduce((sum, b) => sum + mins(ms(b.start_time), ms(b.end_time)), 0);
